import React from "react";
import mainLogo from "../../../Image/Main_logo.png";
import "./PartyCallPrintReport.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const PartyCallAnnualSummaryReport = ({
  party,
  year,
  projectId = "ALL",
  partyProjects = [],
  annualData,
  isScreenPreview = false,
}) => {
  const partyName = party?.CompanyName || "All Parties";
  const partyType = party?.Type || "CLIENT";

  const selectedProjectName = React.useMemo(() => {
    if (projectId === "ALL") return null;
    if (projectId === "NO_PROJECT") return "General";
    if (Array.isArray(partyProjects)) {
      const found = partyProjects.find((p) => String(p?.id) === String(projectId));
      if (found?.name) return found.name;
    }
    return projectId;
  }, [projectId, partyProjects]);

  const nowFormatted = React.useMemo(() => {
    const d = new Date();
    return `${formatDate(d)} at ${formatTime(d)}`;
  }, []);

  const { months = [], totals = { total: 0, completed: 0, pending: 0, other: 0, resolutionRate: "100%" } } =
    annualData || {};

  return (
    <div
      id="print-annual-root"
      className={`print-report-container ${isScreenPreview ? "screen-preview-container" : ""}`}
    >
      <div className={`print-annual-page ${isScreenPreview ? "screen-preview-sheet" : ""}`}>
        {isScreenPreview && (
          <div className="screen-page-tag">
            Annual Report: 12-Month Summary for Year {year}
          </div>
        )}

        {/* Report Header */}
        <div className="print-header">
          <div className="print-header-brand">
            <img
              src={mainLogo}
              alt="Dynamic Softlink"
              className="print-header-logo"
            />
            <div className="print-header-titles">
              <h1>DYNAMIC SOFTLINK &ndash; ANNUAL PARTY CALL SUMMARY</h1>
              <p>12-Month Support Performance &amp; Call Activity Statement</p>
            </div>
          </div>

          <div className="print-header-meta">
            <div className="party-name">
              {partyName}
              <span className="meta-pill">{partyType}</span>
            </div>
            <div>
              <strong>Period:</strong> Entire Year {year}
              {selectedProjectName && (
                <>
                  {" "}
                  &bull; <strong>Project:</strong> {selectedProjectName}
                </>
              )}
            </div>
            <div style={{ fontSize: "8px", color: "#64748b", marginTop: "1px" }}>
              Report Generated: {nowFormatted}
            </div>
          </div>
        </div>

        {/* Annual Overview KPI Banner */}
        <div className="print-annual-kpi-banner">
          <div className="print-annual-kpi-header">
            <div>
              <span className="print-summary-title">
                Annual Support Summary &ndash; Year {year}
              </span>
              <span className="print-summary-subtitle" style={{ marginLeft: "8px" }}>
                &bull; Full 12-Month Performance Overview for {partyName}
              </span>
            </div>
            <div className="print-resolution-badge">
              Annual Resolution Rate: {totals.resolutionRate}
            </div>
          </div>

          <div className="print-summary-grid">
            <div className="print-summary-card card-total">
              <span className="label">Total Annual Calls</span>
              <span className="val">{totals.total}</span>
            </div>
            <div className="print-summary-card card-comp">
              <span className="label">Total Completed</span>
              <span className="val">{totals.completed}</span>
            </div>
            <div className="print-summary-card card-prog">
              <span className="label">In Progress / Review</span>
              <span className="val">{totals.other || 0}</span>
            </div>
            <div className="print-summary-card card-pend">
              <span className="label">Total Pending</span>
              <span className="val">{totals.pending}</span>
            </div>
          </div>
        </div>

        {/* Main Content Area: Side-by-Side 12-Month Table & Mini Cards */}
        <div className="print-annual-body-grid">
          {/* Left Side: 12-Month Summary Table */}
          <div className="print-annual-table-wrapper">
            <div className="print-section-sub-title">
              12-Month Call Performance Table
            </div>
            <table className="print-annual-table">
              <thead>
                <tr>
                  <th style={{ width: "24px" }}>#</th>
                  <th>Month</th>
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Total Calls</th>
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Completed</th>
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>In Progress</th>
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Pending</th>
                  <th style={{ textAlign: "center", whiteSpace: "nowrap" }}>Resolution Rate</th>
                  <th style={{ textAlign: "center", whiteSpace: "nowrap" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m, idx) => {
                  const hasCalls = m.total > 0;
                  const resNum = m.total > 0 ? Math.round((m.completed / m.total) * 100) : 0;
                  const is100 = hasCalls && resNum === 100;
                  const hasPending = m.pending > 0;

                  return (
                    <tr
                      key={m.monthIndex}
                      className={hasCalls ? "row-has-calls" : "row-empty"}
                    >
                      <td style={{ textAlign: "center", color: "#64748b", fontWeight: 700 }}>
                        {idx + 1}
                      </td>
                      <td style={{ fontWeight: hasCalls ? 800 : 600, color: hasCalls ? "#0f172a" : "#64748b" }}>
                        {m.monthName}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 800, color: hasCalls ? "#0284c7" : "#94a3b8" }}>
                        {m.total}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: m.completed > 0 ? "#059669" : "#94a3b8" }}>
                        {m.completed}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: m.other > 0 ? "#d97706" : "#94a3b8" }}>
                        {m.other}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: m.pending > 0 ? "#dc2626" : "#94a3b8" }}>
                        {m.pending}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {hasCalls ? (
                          <span
                            className={`print-table-rate-pill ${
                              is100 ? "rate-100" : hasPending ? "rate-pending" : "rate-normal"
                            }`}
                          >
                            {m.resolutionRate}
                          </span>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>&ndash;</span>
                        )}
                      </td>
                      <td style={{ textAlign: "center", fontSize: "7.5px" }}>
                        {hasCalls ? (
                          is100 ? (
                            <span style={{ color: "#059669", fontWeight: 800 }}>✓ All Done</span>
                          ) : hasPending ? (
                            <span style={{ color: "#dc2626", fontWeight: 800 }}>● Pending</span>
                          ) : (
                            <span style={{ color: "#d97706", fontWeight: 800 }}>◐ Active</span>
                          )
                        ) : (
                          <span style={{ color: "#94a3b8" }}>No Calls</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="print-annual-total-row">
                  <td colSpan={2} style={{ textAlign: "left", fontWeight: 900, textTransform: "uppercase" }}>
                    Annual Totals &amp; Average
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 900, color: "#0284c7" }}>
                    {totals.total}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 900, color: "#059669" }}>
                    {totals.completed}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 900, color: "#d97706" }}>
                    {totals.other || 0}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 900, color: "#dc2626" }}>
                    {totals.pending}
                  </td>
                  <td style={{ textAlign: "center", fontWeight: 900, color: "#065f46" }}>
                    {totals.resolutionRate}
                  </td>
                  <td style={{ textAlign: "center", fontWeight: 900, color: "#154D75" }}>
                    12 Months
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Right Side: 12 Monthly Mini Summary Cards */}
          <div className="print-annual-cards-wrapper">
            <div className="print-section-sub-title">
              Monthly Overview Cards Grid
            </div>
            <div className="print-annual-cards-grid">
              {months.map((m) => {
                const hasCalls = m.total > 0;
                return (
                  <div
                    key={m.monthIndex}
                    className={`print-mini-month-card ${
                      hasCalls ? "card-active" : "card-inactive"
                    }`}
                  >
                    <div className="mini-card-header">
                      <span className="mini-month-name">{m.shortName}</span>
                      <span className="mini-total-badge">
                        {m.total} {m.total === 1 ? "call" : "calls"}
                      </span>
                    </div>

                    <div className="mini-card-body">
                      <div className="mini-stat-item comp">
                        <span className="stat-label">Done</span>
                        <span className="stat-val">{m.completed}</span>
                      </div>
                      <div className="mini-stat-item prog">
                        <span className="stat-label">Prog</span>
                        <span className="stat-val">{m.other}</span>
                      </div>
                      <div className="mini-stat-item pend">
                        <span className="stat-label">Pend</span>
                        <span className="stat-val">{m.pending}</span>
                      </div>
                    </div>

                    <div className="mini-card-footer">
                      <span>Rate:</span>
                      <strong style={{ color: hasCalls ? "#047857" : "#94a3b8" }}>
                        {m.resolutionRate}
                      </strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Annual Footer */}
        <div className="print-page-footer">
          <span>Dynamic Softlink ERP Customer Support System &bull; Annual Performance Statement</span>
          <span>Full Year {year} &bull; Page 1 of 1 (Complete Annual Summary)</span>
        </div>
      </div>
    </div>
  );
};

export default PartyCallAnnualSummaryReport;
